import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Users, Plus, Edit, FileText, HelpCircle, Camera, Loader2, Grid3X3, X } from 'lucide-react';
import { toast } from 'sonner';
import { StudentAvatar, AvatarPicker } from '../components/StudentAvatar';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

// ─── Smart bulk name parser ───
const extractNames = (rawText) => {
  if (!rawText.trim()) return [];

  // Step 1: Normalise line endings
  let text = rawText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Step 2: Replace tabs, pipes, semicolons with newlines (common cell/column separators)
  text = text.replace(/\t/g, '\n').replace(/\|/g, '\n').replace(/;/g, '\n');

  // Step 3: Decide if commas are separators between names or part of "Last, First" format
  const newlines = (text.match(/\n/g) || []).length;
  const commas = (text.match(/,/g) || []).length;

  if (commas >= 2 && commas > newlines) {
    // Check: are comma-chunks mostly multi-word (full names) or single-word (parts)?
    const chunks = text.split(',').map(c => c.trim()).filter(c => c);
    const multiWord = chunks.filter(c => c.includes(' ')).length;
    if (multiWord / chunks.length >= 0.5) {
      // Chunks like "John Smith, Jane Doe" — commas are name separators
      text = text.replace(/,/g, '\n');
    }
    // Otherwise keep commas — likely "Smith, John" format handled later
  }

  // Step 4: Split into candidate lines
  let lines = text.split('\n').map(l => l.trim()).filter(l => l);

  // Step 5: If only 1–2 long lines remain, try splitting by double+ spaces (table cell gaps)
  if (lines.length <= 2) {
    const expanded = [];
    for (const line of lines) {
      if (line.length > 30) {
        const parts = line.split(/\s{2,}/).map(s => s.trim()).filter(s => s);
        if (parts.length > 1) { expanded.push(...parts); continue; }
      }
      expanded.push(line);
    }
    lines = expanded;
  }

  // Step 6: Clean each line — strip numbers, bullets, brackets, extra punctuation
  lines = lines.map(line => {
    return line
      .replace(/^\d+[\.\)\-:\s]+/, '')  // "1. Name" or "1) Name" or "1 - Name"
      .replace(/^[\-\*\u2022\u25CF\u25CB]\s*/, '') // bullet chars
      .replace(/[\[\]\(\)\{\}]/g, '')   // brackets
      .replace(/["'`]/g, '')            // quotes
      .replace(/\s+/g, ' ')            // collapse whitespace
      .trim();
  });

  // Step 7: Filter out non-name content
  lines = lines.filter(line => {
    if (line.length < 2 || line.length > 60) return false;
    if (/^\d+$/.test(line)) return false;
    if (line.includes('@')) return false;
    if (/^https?:/i.test(line)) return false;
    if (/^(students?|names?|first\s?name|last\s?name|surnames?|no\.?|#|id|class|roll|date|period|year|grade|total|teacher|subject|room)/i.test(line)) return false;
    // Must contain at least one letter
    if (!/[a-zA-Z]/.test(line)) return false;
    return true;
  });

  return lines;
};

// Format extracted names into "FirstName LastInitial" display names
const formatNames = (rawNames, sampleLastName) => {
  if (rawNames.length === 0) return [];

  // Detect Last, First vs First Last format
  let isLastNameFirst = false;
  if (sampleLastName && sampleLastName.trim()) {
    const sample = sampleLastName.trim().toLowerCase();
    const firstRaw = rawNames[0];
    // If the first line has a comma and starts with something matching the sample, it's "Last, First"
    if (firstRaw.includes(',')) {
      const beforeComma = firstRaw.split(',')[0].trim().toLowerCase();
      if (beforeComma.startsWith(sample.substring(0, 3)) || sample.startsWith(beforeComma.substring(0, 3))) {
        isLastNameFirst = true;
      }
    } else {
      const parts = firstRaw.split(/\s+/);
      if (parts.length >= 2 && parts[0].toLowerCase().startsWith(sample.substring(0, 3))) {
        isLastNameFirst = true;
      }
    }
  } else {
    // Auto-detect: if most lines have commas, assume "Last, First"
    const commaLines = rawNames.filter(n => n.includes(',')).length;
    if (commaLines > rawNames.length * 0.5) {
      isLastNameFirst = true;
    }
  }

  const parsedNames = rawNames.map(line => {
    let parts;
    if (line.includes(',')) {
      // Split on first comma: "Smith, John Michael" → ["Smith", "John Michael"]
      const [a, b] = line.split(/,\s*(.+)/);
      if (b) {
        parts = isLastNameFirst || !sampleLastName
          ? { firstName: b.trim(), lastName: a.trim() }
          : { firstName: a.trim(), lastName: b.trim() };
      } else {
        const words = a.trim().split(/\s+/);
        parts = words.length === 1
          ? { firstName: words[0], lastName: '' }
          : isLastNameFirst
            ? { firstName: words.slice(1).join(' '), lastName: words[0] }
            : { firstName: words.slice(0, -1).join(' '), lastName: words[words.length - 1] };
      }
    } else {
      const words = line.split(/\s+/).filter(w => w);
      if (words.length === 0) return null;
      if (words.length === 1) return { firstName: words[0], lastName: '' };
      parts = isLastNameFirst
        ? { firstName: words.slice(1).join(' '), lastName: words[0] }
        : { firstName: words.slice(0, -1).join(' '), lastName: words[words.length - 1] };
    }
    return parts;
  }).filter(n => n !== null);

  // Format as "First LastInitial" with disambiguation for duplicates
  const nameCount = {};
  parsedNames.forEach(({ firstName, lastName }) => {
    const key = `${firstName.toLowerCase()}_${lastName ? lastName[0].toLowerCase() : ''}`;
    nameCount[key] = (nameCount[key] || 0) + 1;
  });

  const seenNames = {};
  return parsedNames.map(({ firstName, lastName }) => {
    if (!lastName) return firstName;
    const key = `${firstName.toLowerCase()}_${lastName[0].toLowerCase()}`;
    if (nameCount[key] > 1) {
      seenNames[key] = (seenNames[key] || 0) + 1;
      const letters = Math.min(seenNames[key] + 1, lastName.length);
      const abbr = lastName.substring(0, letters);
      return `${firstName} ${abbr.charAt(0).toUpperCase()}${abbr.slice(1).toLowerCase()}`;
    }
    return `${firstName} ${lastName[0].toUpperCase()}`;
  });
};

export const StudentsPage = () => {
  const { classId } = useParams();
  const [students, setStudents] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [ocrDialogOpen, setOcrDialogOpen] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [newStudentName, setNewStudentName] = useState('');
  const [bulkNames, setBulkNames] = useState('');
  const [sampleLastName, setSampleLastName] = useState('');
  const [previewNames, setPreviewNames] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [avatarStudent, setAvatarStudent] = useState(null);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrPreview, setOcrPreview] = useState(null);
  const [ocrNames, setOcrNames] = useState([]);
  const fileInputRef = useRef(null);

  const fetchData = async () => {
    try {
      const [studentsRes, classesRes] = await Promise.all([
        fetch(`${API}/students/${classId}`, { credentials: 'include' }),
        fetch(`${API}/classes`, { credentials: 'include' })
      ]);
      if (studentsRes.ok) setStudents(await studentsRes.json());
      if (classesRes.ok) {
        const classes = await classesRes.json();
        setClassInfo(classes.find(c => c.class_id === classId));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [classId]);

  useEffect(() => {
    if (bulkNames.trim()) {
      const raw = extractNames(bulkNames);
      const formatted = formatNames(raw, sampleLastName);
      setPreviewNames(formatted);
    } else {
      setPreviewNames([]);
    }
  }, [bulkNames, sampleLastName]);

  const removePreviewName = (index) => {
    setPreviewNames(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateStudent = async () => {
    if (!newStudentName.trim()) { toast.error('Student name is required'); return; }
    try {
      const response = await fetch(`${API}/students`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ class_id: classId, name: newStudentName.trim() })
      });
      if (response.ok) {
        toast.success('Student added successfully');
        setDialogOpen(false); setNewStudentName(''); fetchData();
      }
    } catch (error) { toast.error('Failed to add student'); }
  };

  const handleBulkCreate = async () => {
    if (previewNames.length === 0) { toast.error('Enter student names'); return; }
    try {
      const response = await fetch(`${API}/students/bulk`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ class_id: classId, student_names: previewNames.join('\n') })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Added ${data.created} students`);
        setBulkDialogOpen(false); setBulkNames(''); setSampleLastName(''); setPreviewNames([]); fetchData();
      }
    } catch (error) { toast.error('Failed to add students'); }
  };

  const handleUpdateStudent = async () => {
    if (!editStudent?.name.trim()) { toast.error('Student name is required'); return; }
    try {
      const response = await fetch(`${API}/students/${editStudent.student_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ class_id: classId, name: editStudent.name })
      });
      if (response.ok) { toast.success('Student updated'); setEditStudent(null); fetchData(); }
    } catch (error) { toast.error('Failed to update student'); }
  };

  const handleDeleteStudent = async (studentId) => {
    try {
      const response = await fetch(`${API}/students/${studentId}`, { method: 'DELETE', credentials: 'include' });
      if (response.ok) { toast.success('Student removed'); setEditStudent(null); setConfirmDelete(false); fetchData(); }
      else { const err = await response.json().catch(() => ({})); toast.error(err.detail || 'Failed to remove student'); }
    } catch (error) { toast.error('Failed to remove student'); }
  };

  const handleOCRUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setOcrProcessing(true);
    setOcrNames([]);
    setOcrPreview(URL.createObjectURL(file));
    setOcrDialogOpen(true);

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        try {
          const r = await fetch(`${API}/students/ocr`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ image_base64: base64, class_id: classId }),
          });
          if (r.ok) {
            const data = await r.json();
            setOcrNames(data.names || []);
            toast.success(`Found ${data.count} names`);
          } else {
            const err = await r.json().catch(() => ({}));
            toast.error(err.detail || 'OCR failed');
          }
        } catch (err) {
          toast.error('Failed to process image');
        } finally {
          setOcrProcessing(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error('Failed to read file');
      setOcrProcessing(false);
    }
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleOCRAddStudents = async () => {
    if (ocrNames.length === 0) return;
    // Run through the bulk name processor for privacy
    const processed = formatNames(ocrNames, '');
    try {
      const response = await fetch(`${API}/students/bulk`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ class_id: classId, student_names: processed.join('\n') })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Added ${data.created} students`);
        setOcrDialogOpen(false); setOcrNames([]); setOcrPreview(null); fetchData();
      }
    } catch (error) { toast.error('Failed to add students'); }
  };

  const breadcrumb = [
    { label: 'Dashboard', path: '/app/dashboard' },
    { label: classInfo?.name || 'Class' },
    { label: 'Students' }
  ];

  return (
    <Layout breadcrumb={breadcrumb}>
      <div className="space-y-6" data-testid="students-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-medium text-slate-900">Students</h1>
            <p className="text-slate-500 mt-1">{classInfo?.name} &middot; {students.length} students</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* OCR photo button */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleOCRUpload}
              data-testid="ocr-file-input"
            />
            <Button
              variant="outline"
              className="border-slate-200 text-slate-700 hover:bg-slate-50"
              onClick={() => fileInputRef.current?.click()}
              data-testid="ocr-upload-btn"
            >
              <Camera className="w-4 h-4 mr-2" />
              Scan List
            </Button>
            <Link to={`/app/seating-plan/${classId}`}>
              <Button
                variant="outline"
                className="border-slate-200 text-slate-700 hover:bg-slate-50"
                data-testid="seating-plan-shortcut-btn"
              >
                <Grid3X3 className="w-4 h-4 mr-2" />
                Seating Plan
              </Button>
            </Link>

            <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="bulk-add-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Paste List
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl text-slate-900">Paste Student List</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <HelpCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm text-slate-800 font-medium mb-2">Help identify name format</p>
                        <p className="text-sm text-slate-600 mb-3">Enter any student's last name so we can detect if names are "First Last" or "Last, First" format. Leave blank if unsure.</p>
                        <Input placeholder="e.g., Smith, Johnson, Williams" value={sampleLastName} onChange={(e) => setSampleLastName(e.target.value)} className="border-blue-200 bg-white" data-testid="sample-lastname-input" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Paste student names</label>
                    <p className="text-xs text-slate-400 mt-0.5 mb-1.5">Supports vertical lists, comma-separated, tab-separated, or copied table data</p>
                    <Textarea placeholder={"John Smith, Jane Doe, Alex Johnson\n\nor\n\nSmith, John\nDoe, Jane\n\nor paste from a spreadsheet / database"} value={bulkNames} onChange={(e) => setBulkNames(e.target.value)} className="min-h-[130px] border-slate-200 font-mono text-sm" data-testid="bulk-names-input" />
                  </div>
                  {previewNames.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-slate-700">Preview ({previewNames.length} students)</label>
                        <button onClick={() => setPreviewNames([])} className="text-xs text-slate-400 hover:text-red-500 transition-colors" data-testid="clear-preview-btn">Clear all</button>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3 max-h-[180px] overflow-y-auto">
                        <div className="flex flex-wrap gap-2">
                          {previewNames.map((name, i) => (
                            <span key={i} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 bg-white border border-slate-200 rounded-lg text-sm text-slate-700" data-testid={`preview-name-${i}`}>
                              {name}
                              <button onClick={() => removePreviewName(i)} className="w-5 h-5 rounded-md hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors text-slate-300" data-testid={`remove-name-${i}`}>
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">Tap the X on any name to remove it before importing.</p>
                    </div>
                  )}
                  <Button onClick={handleBulkCreate} disabled={previewNames.length === 0} className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="bulk-create-btn">
                    Add {previewNames.length} Students
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="add-student-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Student
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl text-slate-900">Add New Student</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Student Name *</label>
                    <Input placeholder="Enter student name" value={newStudentName} onChange={(e) => setNewStudentName(e.target.value)} className="mt-1 border-slate-200" data-testid="student-name-input" />
                  </div>
                  <Button onClick={handleCreateStudent} className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="create-student-btn">Add Student</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading...</div>
        ) : students.length === 0 ? (
          <Card className="bg-white border border-dashed border-slate-300 shadow-none">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-800 mb-2">No students yet</h3>
              <p className="text-slate-500 mb-4">Add students individually, paste a list, or scan a photo</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-slate-600 w-14"></TableHead>
                  <TableHead className="text-slate-600">Name</TableHead>
                  <TableHead className="text-slate-600 w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {students.map((student, index) => (
                  <TableRow key={student.student_id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setEditStudent(student)} data-testid={`student-row-${student.student_id}`}>
                    <TableCell>
                      <StudentAvatar student={student} size="sm" onClick={(e) => { e.stopPropagation(); setAvatarStudent(student); }} />
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">{student.name}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" className="text-slate-400 hover:text-teal-600 h-8 px-2" onClick={(e) => { e.stopPropagation(); setEditStudent(student); }} data-testid={`edit-btn-${student.student_id}`}>
                        <Edit className="w-4 h-4 mr-1" /><span className="text-xs">Edit</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        {/* Edit Student Dialog */}
        <Dialog open={!!editStudent} onOpenChange={(open) => { if (!open) { setEditStudent(null); setConfirmDelete(false); } }}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-slate-900">Edit Student</DialogTitle>
            </DialogHeader>
            {editStudent && (
              <div className="space-y-4 pt-4">
                {/* Avatar */}
                <div className="flex items-center justify-center">
                  <StudentAvatar student={editStudent} size="xl" onClick={() => { setAvatarStudent(editStudent); }} className="cursor-pointer" />
                </div>
                <p className="text-xs text-slate-400 text-center">Tap avatar to change</p>
                <div>
                  <label className="text-sm font-medium text-slate-700">Student Name *</label>
                  <Input value={editStudent.name} onChange={(e) => setEditStudent({ ...editStudent, name: e.target.value })} className="mt-1 border-slate-200" data-testid="edit-student-name-input" />
                </div>
                <Button onClick={handleUpdateStudent} className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="update-student-btn">Update Student</Button>
                <div className="border-t border-slate-200 pt-4">
                  {!confirmDelete ? (
                    <Button
                      variant="outline"
                      onClick={() => setConfirmDelete(true)}
                      className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                      data-testid="remove-student-btn"
                    >
                      Remove Student from Class
                    </Button>
                  ) : (
                    <div className="space-y-2" data-testid="confirm-delete-section">
                      <p className="text-sm text-slate-600 text-center">Are you sure? This cannot be undone.</p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setConfirmDelete(false)}
                          className="flex-1 border-slate-300 text-slate-600"
                          data-testid="cancel-delete-btn"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={() => handleDeleteStudent(editStudent.student_id)}
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                          data-testid="confirm-delete-btn"
                        >
                          Yes, Remove
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* OCR Results Dialog */}
        <Dialog open={ocrDialogOpen} onOpenChange={(open) => { if (!open) { setOcrDialogOpen(false); setOcrNames([]); setOcrPreview(null); } }}>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-slate-900">Scanned Student List</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              {ocrPreview && (
                <div className="rounded-xl overflow-hidden border border-slate-200 max-h-40">
                  <img src={ocrPreview} alt="Scanned list" className="w-full h-full object-cover" data-testid="ocr-preview-image" />
                </div>
              )}
              {ocrProcessing ? (
                <div className="flex items-center justify-center gap-2 py-8 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin" /> Extracting names...
                </div>
              ) : ocrNames.length > 0 ? (
                <>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Extracted names ({ocrNames.length})</label>
                    <div className="bg-slate-50 rounded-lg p-3 max-h-[200px] overflow-y-auto">
                      <div className="flex flex-wrap gap-2">
                        {ocrNames.map((name, i) => (
                          <span key={i} className="px-2 py-1 bg-white border border-slate-200 rounded text-sm text-slate-700" data-testid={`ocr-name-${i}`}>{name}</span>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">Last names will be shortened to initials for privacy.</p>
                  </div>
                  <Button onClick={handleOCRAddStudents} className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="ocr-add-students-btn">
                    Add {ocrNames.length} Students
                  </Button>
                </>
              ) : (
                <p className="text-center py-4 text-slate-400 text-sm" data-testid="ocr-no-names">No names found. Try a clearer image.</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Avatar Picker */}
      {avatarStudent && (
        <AvatarPicker
          student={avatarStudent}
          onClose={() => setAvatarStudent(null)}
          onUpdate={(updated) => {
            setStudents(prev => prev.map(s => s.student_id === updated.student_id ? { ...s, ...updated } : s));
            if (editStudent?.student_id === updated.student_id) setEditStudent(prev => ({ ...prev, ...updated }));
          }}
        />
      )}
    </Layout>
  );
};
